import { Helmet } from 'react-helmet-async';
import { generateLocalBusinessSchema } from '@/utils/seo';
import { ProfileView } from './components/ProfileView';
import { bookingWizardSearch } from './navigation';
import type { ProfileDisplayOptions, PublicOrganization, PublicService } from './types';

const SCHEMA_DAYS: Record<string, string> = { mon: 'Mo', tue: 'Tu', wed: 'We', thu: 'Th', fri: 'Fr', sat: 'Sa', sun: 'Su' };

function seoSchema(organization: PublicOrganization) {
  const page = organization.booking_page;
  const base = generateLocalBusinessSchema({
    businessType: organization.business_type || 'LocalBusiness',
    name: organization.name,
    description: organization.description || organization.name,
  }) as Record<string, unknown>;
  if (page.location) {
    base.address = page.location.address;
    if (page.location.latitude != null) {
      base.geo = { '@type': 'GeoCoordinates', latitude: page.location.latitude, longitude: page.location.longitude };
    }
  }
  if (page.phone) base.telephone = page.phone;
  if (page.hours) {
    base.openingHours = page.hours.days.flatMap((d) => d.ranges.map((r) => `${SCHEMA_DAYS[d.day]} ${r.from}-${r.to}`));
  }
  if (page.photos[0]?.large_url) base.image = page.photos[0].large_url;
  return base;
}

interface BookingProfilePageProps {
  // As saved (SEO), and as shown (with the admin preview draft, if any).
  organization: PublicOrganization;
  shown: PublicOrganization;
  services: PublicService[];
  options: ProfileDisplayOptions;
  search: string;
  onNavigate: (search: string) => void;
}

// Step 0: the profile content inside BookingPageFrame (see book.tsx).
export function BookingProfilePage({ organization, shown, services, options, search, onNavigate }: BookingProfilePageProps) {
  const book = (service: PublicService) => onNavigate(bookingWizardSearch(service.id, search));
  const description = organization.booking_page.tagline || organization.description || organization.name;
  const cover = organization.booking_page.photos[0]?.large_url || organization.logo_url || 'https://slotyapp.com/og-image.jpg';

  return (
    <>
      <Helmet>
        <title>{`${organization.name} | Sloty`}</title>
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={organization.name} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={cover} />
        {options.embedded && <meta name="robots" content="noindex" />}
        <script type="application/ld+json">{JSON.stringify(seoSchema(organization))}</script>
      </Helmet>
      <ProfileView organization={shown} services={services} options={options} onBook={book} />
    </>
  );
}
